// "What Neo remembers" on the Profile page. Three kinds of memory, all the
// signed-in owner's alone:
//
//   - Saved notes (ADR 0226): what the owner asked the assistant to keep across
//     conversations, each with a Forget button. The assistant writes notes only
//     when asked, in the chat. This list is the owner's control over them:
//     deleting one here deletes it everywhere. It refetches when a turn on any
//     surface reports a memory change (NEO_MEMORIES_UPDATED_EVENT).
//   - Past conversations (ADR 0250): short summaries the cloud writes once a
//     chat has gone quiet, each with a Forget button, under a switch that turns
//     the feature off and erases them all (after a confirm). Each of the
//     owner's own chat turns reads a few of them into its answer (TOM-252).
//     The list refetches when a chat turn forgets one (NEO_RECALL_UPDATED_EVENT,
//     TOM-253).
//   - Learned about you (TOM-254, ADR 0258): the lasting facts the cloud
//     folds out of those summaries, each with its kind, its boat and a Forget
//     button. The same switch erases them. Each of the owner's own chat turns
//     reads them into its answer too. They refetch with the summaries, so a
//     chat turn that forgot a summary, or everything learned, drops them here.

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { api } from '../api';
import { NEO_MEMORIES_UPDATED_EVENT } from '../lib/neoMemories';
import {
  NEO_PROFILE_EMPTY,
  NEO_PROFILE_HELP,
  NEO_RECALL_FIRST_PAGE,
  NEO_RECALL_HELP,
  NEO_RECALL_UPDATED_EVENT,
  episodeMeta,
  forgetNeoProfileItem,
  forgetNeoRecallEpisode,
  loadNeoRecall,
  profileItemMeta,
  recallAfterSave,
  recallOffConfirm,
  recallStatusLine,
  recallSwitchPress,
  recallWithProfileFrom,
  recallWithoutEpisode,
  recallWithoutProfileItem,
  saveNeoRecallEnabled,
  type NeoRecallState,
} from '../lib/neoRecall';

interface NeoMemory {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

function formatSaved(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function NeoMemorySection() {
  const [memories, setMemories] = useState<NeoMemory[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [forgetting, setForgetting] = useState<string | null>(null);
  const [forgetError, setForgetError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const body = await api<{ memories: NeoMemory[] }>('/v1/neo/memories');
      setMemories(body.memories);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load();
    const onUpdated = () => void load();
    window.addEventListener(NEO_MEMORIES_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(NEO_MEMORIES_UPDATED_EVENT, onUpdated);
  }, [load]);

  async function forget(id: string) {
    setForgetting(id);
    setForgetError(null);
    try {
      await api(`/v1/neo/memories/${id}`, { method: 'DELETE' });
      setMemories((prev) => (prev === null ? prev : prev.filter((m) => m.id !== id)));
    } catch (err) {
      const e = err as { status?: number };
      if (e.status === 404) {
        // Already gone (forgotten in a chat meanwhile): the list was stale.
        setMemories((prev) => (prev === null ? prev : prev.filter((m) => m.id !== id)));
      } else {
        setForgetError('Couldn’t forget that note. Please try again.');
      }
    } finally {
      setForgetting(null);
    }
  }

  return (
    <div className="profile-section" data-testid="neo-memory-section">
      <h3 className="profile-section-heading">What Neo remembers</h3>
      <p className="profile-section-help">
        Only you can see this, even on a boat you share with other owners.
      </p>

      <h4 className="neo-memory-subheading">Saved notes</h4>
      <p className="neo-memory-subhelp">
        Notes you asked Neo to keep across conversations. Neo uses them in every chat. Ask Neo to
        remember or forget something, or remove a note here.
      </p>
      {loadError ? (
        <p className="dashboard-muted" role="alert">
          Couldn’t load what Neo remembers.
        </p>
      ) : memories === null ? (
        <p className="dashboard-muted">Loading…</p>
      ) : memories.length === 0 ? (
        <p className="dashboard-muted" data-testid="neo-memory-empty">
          Nothing yet. In any chat with Neo, say something like “remember that I keep the boat at
          Shilshole” or “from now on, keep your answers short”.
        </p>
      ) : (
        <ul className="neo-memory-list" data-testid="neo-memory-list">
          {memories.map((m) => (
            <li key={m.id} className="neo-memory-item">
              <div className="neo-memory-text">
                <p>{m.content}</p>
                <span className="neo-memory-date">Saved {formatSaved(m.updatedAt)}</span>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={forgetting !== null}
                onClick={() => void forget(m.id)}
                aria-label={`Forget: ${m.content}`}
              >
                {forgetting === m.id ? 'Forgetting…' : 'Forget'}
              </button>
            </li>
          ))}
        </ul>
      )}
      {forgetError !== null ? (
        <p className="form-errors" role="alert">
          {forgetError}
        </p>
      ) : null}

      <PastConversations />
    </div>
  );
}

/**
 * The summaries of past chats, under the switch that keeps them, and then
 * what Neo learned from them. No optimistic UI on the switch: it shows
 * what the server answered, and a failed save re-reads the server rather than
 * guessing what happened.
 *
 * Forgetting a summary hides, on the server, every learned item it was a
 * source of, so the learned list is re-read after one.
 *
 * Switching off asks first, in an inline confirm. Focus moves to its Cancel
 * button when it opens (the switch is disabled meanwhile, so focus would
 * otherwise drop to the page), Escape cancels, and focus returns to the switch
 * when it closes, as BoatFirmwareBanner's confirm does.
 */
function PastConversations() {
  const [recall, setRecall] = useState<NeoRecallState | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [forgetting, setForgetting] = useState<string | null>(null);
  const [forgetError, setForgetError] = useState<string | null>(null);
  const [forgettingItem, setForgettingItem] = useState<string | null>(null);
  const [itemForgetError, setItemForgetError] = useState<string | null>(null);
  const [confirmingOff, setConfirmingOff] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const switchButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!confirmingOff) return;
    const frame = window.requestAnimationFrame(() => cancelButton.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [confirmingOff]);

  /** Close the confirm and put focus back on the switch. */
  const closeConfirm = useCallback(() => {
    setConfirmingOff(false);
    window.requestAnimationFrame(() => switchButton.current?.focus());
  }, []);

  const load = useCallback(async () => {
    try {
      setRecall(await loadNeoRecall());
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load();
    // A chat turn forgot a summary (TOM-253): drop it here too.
    const onUpdated = () => void load();
    window.addEventListener(NEO_RECALL_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(NEO_RECALL_UPDATED_EVENT, onUpdated);
  }, [load]);

  async function forget(id: string) {
    setForgetting(id);
    setForgetError(null);
    try {
      // A summary already gone resolves too: the list was stale.
      await forgetNeoRecallEpisode(id);
      setRecall((prev) => recallWithoutEpisode(prev, id));
    } catch {
      setForgetError('Couldn’t forget that summary. Please try again.');
      return;
    } finally {
      setForgetting(null);
    }
    // The learned items it was a source of are hidden now. A failed re-read
    // keeps them listed until the next load; forgetting one then is a 404,
    // which resolves.
    try {
      const next = await loadNeoRecall();
      setRecall((prev) => recallWithProfileFrom(prev, next));
    } catch {
      // The summary is forgotten either way; the next load drops the items.
    }
  }

  async function forgetItem(id: string) {
    setForgettingItem(id);
    setItemForgetError(null);
    try {
      // An item already gone resolves too: the list was stale.
      await forgetNeoProfileItem(id);
      setRecall((prev) => recallWithoutProfileItem(prev, id));
    } catch {
      setItemForgetError('Couldn’t forget that. Please try again.');
    } finally {
      setForgettingItem(null);
    }
  }

  async function save(enabled: boolean) {
    setSwitching(true);
    setSwitchError(null);
    try {
      const settings = await saveNeoRecallEnabled(enabled);
      setRecall((prev) => recallAfterSave(prev, settings));
      if (!enabled) closeConfirm();
    } catch {
      setSwitchError('Couldn’t save that. Please try again.');
      // The reply was lost, not necessarily the change: show what the server has.
      await load();
    } finally {
      setSwitching(false);
    }
  }

  function onSwitch() {
    // Off erases every summary and learned item: a press while on only opens
    // the confirm.
    const press = recallSwitchPress(recall, switching);
    if (press === 'confirm_off') {
      setSwitchError(null);
      setConfirmingOff(true);
    } else if (press === 'turn_on') {
      void save(true);
    }
  }

  function onConfirmKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' && !switching) {
      event.preventDefault();
      closeConfirm();
    }
  }

  const episodes = recall?.episodes ?? [];
  const profile = recall?.profile ?? [];
  const visible = showAll ? episodes : episodes.slice(0, NEO_RECALL_FIRST_PAGE);
  const confirm = recallOffConfirm(episodes.length, profile.length);

  return (
    <>
      <div className="neo-recall" data-testid="neo-recall-section">
        <h4 className="neo-memory-subheading">Past conversations</h4>
        <p className="neo-memory-subhelp">{NEO_RECALL_HELP}</p>

        <div className="neo-recall-switch-row">
          <div className="neo-recall-switch-text">
            <span id="neo-recall-switch-label" className="neo-recall-switch-label">
              Remember past conversations
            </span>
            {recall !== null ? (
              <span className="neo-memory-date" data-testid="neo-recall-status">
                {recallStatusLine(recall)}
              </span>
            ) : null}
          </div>
          <button
            ref={switchButton}
            type="button"
            className={`neo-recall-switch${recall?.enabled ? ' neo-recall-switch--on' : ''}`}
            role="switch"
            aria-checked={recall?.enabled ?? false}
            aria-labelledby="neo-recall-switch-label"
            disabled={recall === null || switching || confirmingOff}
            data-testid="neo-recall-switch"
            onClick={onSwitch}
          >
            <span className="neo-recall-switch__knob" aria-hidden="true" />
          </button>
        </div>

        {confirmingOff ? (
          <div
            className="neo-recall-confirm"
            role="group"
            aria-labelledby="neo-recall-confirm-title"
            aria-describedby="neo-recall-confirm-body"
            data-testid="neo-recall-confirm"
            onKeyDown={onConfirmKeyDown}
          >
            <p id="neo-recall-confirm-title" className="neo-recall-confirm-title">
              {confirm.title}
            </p>
            <p id="neo-recall-confirm-body" className="neo-recall-confirm-body">
              {confirm.body}
            </p>
            <div className="neo-recall-confirm-actions">
              <button
                type="button"
                className="btn btn-danger"
                disabled={switching}
                onClick={() => void save(false)}
                data-testid="neo-recall-confirm-off"
              >
                {switching ? 'Turning off…' : confirm.action}
              </button>
              <button
                ref={cancelButton}
                type="button"
                className="btn btn-secondary"
                disabled={switching}
                onClick={closeConfirm}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
        {switchError !== null ? (
          <p className="form-errors" role="alert">
            {switchError}
          </p>
        ) : null}

        {loadError ? (
          <p className="dashboard-muted" role="alert">
            Couldn’t load your past conversations.
          </p>
        ) : recall === null ? (
          <p className="dashboard-muted">Loading…</p>
        ) : !recall.enabled ? null : episodes.length === 0 ? (
          <p className="dashboard-muted" data-testid="neo-recall-empty">
            Nothing yet. A summary appears here about half an hour after a chat with Neo goes quiet.
          </p>
        ) : (
          <>
            <ul className="neo-memory-list" data-testid="neo-recall-list">
              {visible.map((e) => (
                <li key={e.id} className="neo-memory-item neo-recall-item">
                  <div className="neo-memory-text">
                    <p className="neo-recall-title">{e.title}</p>
                    <span className="neo-memory-date">{episodeMeta(e)}</span>
                    <p className="neo-recall-gist">{e.gist}</p>
                    {e.ownerFacts.length > 0 ? (
                      <>
                        <p className="neo-recall-label">What you mentioned</p>
                        <ul className="neo-recall-points">
                          {e.ownerFacts.map((fact, i) => (
                            <li key={i}>{fact}</li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                    {e.openThreads.length > 0 ? (
                      <>
                        <p className="neo-recall-label">Left open</p>
                        <ul className="neo-recall-points">
                          {e.openThreads.map((thread, i) => (
                            <li key={i}>{thread}</li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={forgetting !== null}
                    onClick={() => void forget(e.id)}
                    aria-label={`Forget: ${e.title}`}
                  >
                    {forgetting === e.id ? 'Forgetting…' : 'Forget'}
                  </button>
                </li>
              ))}
            </ul>
            {episodes.length > visible.length ? (
              <button
                type="button"
                className="btn btn-secondary neo-recall-more"
                onClick={() => setShowAll(true)}
              >
                Show all {episodes.length}
              </button>
            ) : null}
          </>
        )}
        {forgetError !== null ? (
          <p className="form-errors" role="alert">
            {forgetError}
          </p>
        ) : null}
      </div>

      {recall !== null && recall.enabled && !loadError ? (
        <div className="neo-recall" data-testid="neo-profile-section">
          <h4 className="neo-memory-subheading">Learned about you</h4>
          <p className="neo-memory-subhelp">{NEO_PROFILE_HELP}</p>
          {profile.length === 0 ? (
            <p className="dashboard-muted" data-testid="neo-profile-empty">
              {NEO_PROFILE_EMPTY}
            </p>
          ) : (
            <ul className="neo-memory-list" data-testid="neo-profile-list">
              {profile.map((item) => {
                const meta = profileItemMeta(item);
                return (
                  <li key={item.id} className="neo-memory-item">
                    <div className="neo-memory-text">
                      <p>{item.text}</p>
                      {meta !== '' ? <span className="neo-memory-date">{meta}</span> : null}
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={forgettingItem !== null}
                      onClick={() => void forgetItem(item.id)}
                      aria-label={`Forget: ${item.text}`}
                    >
                      {forgettingItem === item.id ? 'Forgetting…' : 'Forget'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {itemForgetError !== null ? (
            <p className="form-errors" role="alert">
              {itemForgetError}
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
